import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  IconButton,
  Toolbar,
  AppBar,
  useTheme,
  useMediaQuery,
  Button,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  Api as ApiIcon,
  Lock as LockIcon,
  Close as CloseIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import NavBar from "../NavBar";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SIALogo from "../../assets/SIALogo.png";

const AdminNavigation = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigationItems = [
    {
      text: "Dashboard",
      icon: <DashboardIcon />,
      path: "/admin/dashboard",
      componentKey: "ADMIN_ROLES_PAGE",
    },
    {
      text: "User Management",
      icon: <PeopleIcon />,
      path: "/admin/users",
      componentKey: "ADMIN_ROLES_PAGE",
    },
    {
      text: "Role Management",
      icon: <SecurityIcon />,
      path: "/admin/roles",
      componentKey: "ADMIN_ROLES_PAGE",
    },
    {
      text: "UI Permissions",
      icon: <VisibilityIcon />,
      path: "/admin/ui-permissions",
      componentKey: "ADMIN_UI_PERMISSIONS_PAGE",
    },
    {
      text: "API Management",
      icon: <ApiIcon />,
      path: "/admin/api-management",
      componentKey: "ADMIN_API_MANAGEMENT_PAGE",
    },
    {
      text: "Code Values",
      icon: <CodeIcon />,
      path: "/admin/code-values",
      componentKey: "ADMIN_CODE_VALUES_PAGE",
    },
    {
      text: "Password Management",
      icon: <LockIcon />,
      path: "/admin/passwords",
      componentKey: "ADMIN_PASSWORDS_PAGE",
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ width: 240 }}>
      <Toolbar>
        {isMobile && (
          <IconButton edge="start" color="inherit" aria-label="logo">
            <Box
              component="img"
              src={SIALogo}
              alt="sia logo"
              sx={{ paddingLeft: "8px", width: "40px" }}
            />
          </IconButton>
        )}
        <Typography
          variant="h6"
          sx={{ fontSize: { xs: "15px", md: "20px" }, marginLeft: "30px" }}
          noWrap
          component="div"
        >
          Admin Panel
        </Typography>
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="close drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ ml: "auto" }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => handleNavigation(item.path)}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "#dae1ef",
                  color: "#17479e",
                  "&:hover": {
                    backgroundColor: "#dae1ef",
                  },
                  "& .MuiListItemIcon-root": {
                    color: "#17479e",
                  },
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <>
        <NavBar
          sx={{ position: "fixed" }}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
        />
      </>

      <Box sx={{ display: "flex" }}>
        {/* ===== Mobile App Bar (below NavBar) ===== */}
        {/* {isMobile && (
          <AppBar
            position="fixed"
            sx={{
              top: "85px", // height of NavBar (adjust if different)
              width: "100%",
              display: { md: "none" },
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div">
                Admin Management
              </Typography>
            </Toolbar>
          </AppBar>
        )} */}

        {/* ===== Drawer (Sidebar) ===== */}
        <Box
          component="nav"
          sx={{
            width: { md: 220 },
            flexShrink: { md: 0 },
            // Drawer below AppBar
          }}
          aria-label="admin navigation"
        >
          <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={isMobile ? mobileOpen : true}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile
            }}
            sx={{
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                marginTop: { xs: 0, md: "85px" },

                // Always fits to visible screen height
                height: { xs: "100dvh", md: "calc(100dvh - 85px)" },

                //  Allow scroll if content is taller
                overflowY: "auto",
              },
            }}
          >
            {drawer}
            {/* === Drawer Bottom Section === */}
            <Box
              sx={{
                p: 2,
                borderTop: "1px solid #e0e0e0",
                display: "flex",
                bottom: "0",
                top: "0px",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
              onClick={() => navigate("/booklist")} // 👈 navigate to previous page
            >
              <Button
                startIcon={<ArrowBackIcon />}
                color="primary"
                variant="text"
                sx={{ textTransform: "none", fontWeight: 500 }}
              >
                Back
              </Button>
            </Box>
          </Drawer>
        </Box>

        {/* ===== Main Content ===== */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            width: { md: `calc(100% - 280px)` },
            mt: { xs: isMobile ? 7 : 0, md: 8 }, // space for both NavBar + AppBar
            backgroundColor: "#f9f9f9",
          }}
        >
          {children}
        </Box>
      </Box>
    </>
  );
};

export default AdminNavigation;
